import { Directionality } from '@angular/cdk/bidi';
import * as i0 from '@angular/core';
import { InjectionToken, input, booleanAttribute, inject, ElementRef, signal, Component, ChangeDetectionStrategy, computed, afterNextRender, Directive, TemplateRef, Input, ContentChildren, NgModule } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { __esDecorate, __runInitializers } from 'tslib';
import { NgTemplateOutlet } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import { NzStringTemplateOutletDirective } from 'ng-zorro-antd/core/outlet';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["*"];
const _c1 = a0 => ({
  $implicit: a0
});
function NzSpaceComponent_For_2_Conditional_2_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵtextInterpolate(ctx_r0.nzSplit);
  }
}
function NzSpaceComponent_For_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 3);
    i0.ɵɵtemplate(1, NzSpaceComponent_For_2_Conditional_2_ng_template_1_Template, 1, 1, "ng-template", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const ɵ$index_2_r3 = ctx_r1.$index;
    const ɵ$count_2_r4 = ctx_r1.$count;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("margin-bottom", ctx_r0.nzDirection === "vertical" ? ɵ$index_2_r3 === ɵ$count_2_r4 - 1 ? null : ctx_r0.spaceSize : null, "px")("margin-right", ctx_r0.nzDirection === "horizontal" ? ɵ$index_2_r3 === ɵ$count_2_r4 - 1 ? null : ctx_r0.spaceSize : null, "px");
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzSplit)("nzStringTemplateOutletContext", i0.ɵɵpureFunction1(6, _c1, ɵ$index_2_r3));
  }
}
function NzSpaceComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 0);
    i0.ɵɵelementContainer(1, 1);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(2, NzSpaceComponent_For_2_Conditional_2_Template, 2, 8, "span", 2);
  }
  if (rf & 2) {
    const item_r5 = ctx.$implicit;
    const ɵ$index_2_r3 = ctx.$index;
    const ɵ$count_2_r4 = ctx.$count;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("margin-bottom", ctx_r0.nzDirection === "vertical" ? ɵ$index_2_r3 === ɵ$count_2_r4 - 1 ? null : ctx_r0.spaceSize : null, "px")("margin-right", ctx_r0.nzDirection === "horizontal" ? ɵ$index_2_r3 === ɵ$count_2_r4 - 1 ? null : ctx_r0.spaceSize : null, "px");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", item_r5);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.nzSplit && !(ɵ$index_2_r3 === ɵ$count_2_r4 - 1) ? 2 : -1);
  }
}
const NZ_SPACE_COMPACT_SIZE = new InjectionToken('NZ_SPACE_COMPACT_SIZE');
const NZ_SPACE_COMPACT_ITEMS = new InjectionToken('NZ_SPACE_COMPACT_ITEMS');
const NZ_SPACE_COMPACT_ITEM_TYPE = new InjectionToken('NZ_SPACE_COMPACT_ITEM_TYPE');

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSpaceCompactComponent = /*#__PURE__*/(() => {
  class NzSpaceCompactComponent {
    constructor() {
      this.nzBlock = input(false, {
        transform: booleanAttribute
      });
      this.nzDirection = input('horizontal');
      this.nzSize = input('default');
      this.elementRef = inject(ElementRef);
    }
    static {
      this.ɵfac = function NzSpaceCompactComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpaceCompactComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSpaceCompactComponent,
        selectors: [["nz-space-compact"]],
        hostAttrs: [1, "ant-space-compact"],
        hostVars: 4,
        hostBindings: function NzSpaceCompactComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-space-compact-block", ctx.nzBlock())("ant-space-compact-vertical", ctx.nzDirection() === "vertical");
          }
        },
        inputs: {
          nzBlock: [1, "nzBlock"],
          nzDirection: [1, "nzDirection"],
          nzSize: [1, "nzSize"]
        },
        exportAs: ["nzSpaceCompact"],
        features: [i0.ɵɵProvidersFeature([{
          provide: NZ_SPACE_COMPACT_SIZE,
          useFactory: () => inject(NzSpaceCompactComponent).nzSize
        }, {
          provide: NZ_SPACE_COMPACT_ITEMS,
          useFactory: () => signal([])
        }])],
        ngContentSelectors: _c0,
        decls: 1,
        vars: 0,
        template: function NzSpaceCompactComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
          }
        },
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return NzSpaceCompactComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSpaceCompactItemDirective = /*#__PURE__*/(() => {
  class NzSpaceCompactItemDirective {
    get parentElement() {
      return this.elementRef.nativeElement?.parentElement;
    }
    constructor() {
      /**
       * Ancestor component injected from the parent.
       * Note that it is not necessarily the direct parent component.
       */
      this.spaceCompactCmp = inject(NzSpaceCompactComponent, {
        host: true,
        optional: true
      });
      this.items = inject(NZ_SPACE_COMPACT_ITEMS, {
        host: true,
        optional: true
      });
      this.type = inject(NZ_SPACE_COMPACT_ITEM_TYPE);
      this.elementRef = inject(ElementRef);
      this.directionality = inject(Directionality);
      this.dir = toSignal(this.directionality.change, {
        initialValue: this.directionality.value
      });
      this.class = computed(() => {
        // Only handle when the parent is space compact component
        if (!this.spaceCompactCmp || !this.items) return null;
        // Ensure that the injected ancestor component's elements are parent elements
        if (this.parentElement !== this.spaceCompactCmp.elementRef.nativeElement) return null;
        const items = this.items();
        const direction = this.spaceCompactCmp.nzDirection();
        const classes = [compactItemClassOf(this.type, direction, this.dir() === 'rtl')];
        const index = items.indexOf(this);
        const firstIndex = items.findIndex(element => element);
        // Array [empty, item]
        // In this case, the index of the first valid element is not 0,
        // so we need to use findIndex to find the index value of the first valid element.
        if (index === firstIndex) {
          classes.push(compactFirstItemClassOf(this.type, direction));
        } else if (index === items.length - 1) {
          classes.push(compactLastItemClassOf(this.type, direction));
        }
        return classes;
      });
      if (!this.spaceCompactCmp || !this.items) return;
      afterNextRender(() => {
        // Ensure that the injected ancestor component's elements are parent elements
        if (this.parentElement === this.spaceCompactCmp.elementRef.nativeElement) {
          const index = Array.from(this.parentElement.children).indexOf(this.elementRef.nativeElement);
          this.items.update(value => {
            const newValue = value.slice();
            newValue.splice(index, 0, this);
            return newValue;
          });
        }
      });
    }
    ngOnDestroy() {
      this.items?.update(value => value.filter(o => o !== this));
    }
    static {
      this.ɵfac = function NzSpaceCompactItemDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpaceCompactItemDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NzSpaceCompactItemDirective,
        hostVars: 2,
        hostBindings: function NzSpaceCompactItemDirective_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassMap(ctx.class());
          }
        },
        exportAs: ["nzSpaceCompactItem"]
      });
    }
  }
  return NzSpaceCompactItemDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function generateCompactClass(type, direction, position) {
  const directionPrefix = direction === 'vertical' ? 'vertical-' : '';
  return `ant-${type}-compact-${directionPrefix}${position}`;
}
function compactItemClassOf(type, direction, rtl) {
  const rtlSuffix = rtl ? '-rtl' : '';
  return `${generateCompactClass(type, direction, 'item')}${rtlSuffix}`;
}
function compactFirstItemClassOf(type, direction) {
  return generateCompactClass(type, direction, 'first-item');
}
function compactLastItemClassOf(type, direction) {
  return generateCompactClass(type, direction, 'last-item');
}

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSpaceItemDirective = /*#__PURE__*/(() => {
  class NzSpaceItemDirective {
    static {
      this.ɵfac = function NzSpaceItemDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpaceItemDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NzSpaceItemDirective,
        selectors: [["", "nzSpaceItem", ""]]
      });
    }
  }
  return NzSpaceItemDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NZ_CONFIG_MODULE_NAME = 'space';
const SPACE_SIZE = {
  small: 8,
  middle: 16,
  large: 24
};
let NzSpaceComponent = (() => {
  let _nzSize_decorators;
  let _nzSize_initializers = [];
  let _nzSize_extraInitializers = [];
  return class NzSpaceComponent {
    static {
      const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
      _nzSize_decorators = [WithConfig()];
      __esDecorate(null, null, _nzSize_decorators, {
        kind: "field",
        name: "nzSize",
        static: false,
        private: false,
        access: {
          has: obj => "nzSize" in obj,
          get: obj => obj.nzSize,
          set: (obj, value) => {
            obj.nzSize = value;
          }
        },
        metadata: _metadata
      }, _nzSize_initializers, _nzSize_extraInitializers);
      if (_metadata) Object.defineProperty(this, Symbol.metadata, {
        enumerable: true,
        configurable: true,
        writable: true,
        value: _metadata
      });
    }
    constructor(nzConfigService, cdr) {
      this.nzConfigService = nzConfigService;
      this.cdr = cdr;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzDirection = 'horizontal';
      this.nzSplit = null;
      this.nzWrap = false;
      this.nzSize = __runInitializers(this, _nzSize_initializers, 'small');
      this.items = __runInitializers(this, _nzSize_extraInitializers);
      this.spaceSize = SPACE_SIZE.small;
      this.destroy$ = new Subject();
    }
    updateSpaceItems() {
      const numberSize = typeof this.nzSize === 'string' ? SPACE_SIZE[this.nzSize] : this.nzSize;
      this.spaceSize = numberSize / (this.nzSplit ? 2 : 1);
      this.cdr.markForCheck();
    }
    ngOnChanges() {
      this.updateSpaceItems();
      this.mergedAlign = this.nzAlign === undefined && this.nzDirection === 'horizontal' ? 'center' : this.nzAlign;
    }
    ngOnDestroy() {
      this.destroy$.next(true);
      this.destroy$.complete();
    }
    ngAfterContentInit() {
      this.updateSpaceItems();
      this.items.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
    static {
      this.ɵfac = function NzSpaceComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpaceComponent)(i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSpaceComponent,
        selectors: [["nz-space"], ["", "nz-space", ""]],
        contentQueries: function NzSpaceComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, NzSpaceItemDirective, 4, TemplateRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.items = _t);
          }
        },
        hostAttrs: [1, "ant-space"],
        hostVars: 14,
        hostBindings: function NzSpaceComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵstyleProp("flex-wrap", ctx.nzWrap ? "wrap" : null);
            i0.ɵɵclassProp("ant-space-horizontal", ctx.nzDirection === "horizontal")("ant-space-vertical", ctx.nzDirection === "vertical")("ant-space-align-start", ctx.mergedAlign === "start")("ant-space-align-end", ctx.mergedAlign === "end")("ant-space-align-center", ctx.mergedAlign === "center")("ant-space-align-baseline", ctx.mergedAlign === "baseline");
          }
        },
        inputs: {
          nzDirection: "nzDirection",
          nzAlign: "nzAlign",
          nzSplit: "nzSplit",
          nzWrap: [2, "nzWrap", "nzWrap", booleanAttribute],
          nzSize: "nzSize"
        },
        exportAs: ["nzSpace"],
        features: [i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c0,
        decls: 3,
        vars: 0,
        consts: [[1, "ant-space-item"], [3, "ngTemplateOutlet"], [1, "ant-space-split", 3, "margin-bottom", "margin-right"], [1, "ant-space-split"], [3, "nzStringTemplateOutlet", "nzStringTemplateOutletContext"]],
        template: function NzSpaceComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
            i0.ɵɵrepeaterCreate(1, NzSpaceComponent_For_2_Template, 3, 6, null, null, i0.ɵɵrepeaterTrackByIdentity);
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵrepeater(ctx.items);
          }
        },
        dependencies: [NgTemplateOutlet, NzStringTemplateOutletDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  };
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSpaceModule = /*#__PURE__*/(() => {
  class NzSpaceModule {
    static {
      this.ɵfac = function NzSpaceModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NzSpaceModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzSpaceModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return NzSpaceModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NZ_SPACE_COMPACT_ITEMS, NZ_SPACE_COMPACT_ITEM_TYPE, NZ_SPACE_COMPACT_SIZE, NzSpaceCompactComponent, NzSpaceCompactItemDirective, NzSpaceComponent, NzSpaceItemDirective, NzSpaceModule };
